import React, { useMemo } from "react";
import InteractionCard from "./InteractionCard";
import BantSummary from "./BantSummary";
import { Spinner } from "@heroui/react";
import TextButton from "@/shared/components/ui/Button/TextButton";
import { useFetchAccountInteractions } from "../../hooks/useAccountConversations";

const sentimentClass = (label = "") => {
  const normalized = label.toLowerCase();
  if (normalized.includes("positive")) {
    return "text-success-500 bg-success-25";
  }
  if (normalized.includes("negative")) {
    return "text-error-500 bg-error-50";
  }
  return "text-warning-500 bg-warning-50";
};

const AccountManagement = ({
  className = "",
  companyName = "",
  contacts = [],
  interactions = [],
  summary = null,
  sentiment = null,
  bant = null,
  isLoading = false,
  isSummaryLoading = false,
  // { onConnect, isConnecting } when meetings need a Microsoft connection; else null.
  connectMeetings = null,
}) => {
  const {
    data: channelResponse,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchAccountInteractions({
    companyName,
    sourceType: null,
    enabled: Boolean(companyName),
  });

  const displayedInteractions = useMemo(() => {
    const channelInteractions = (channelResponse?.pages ?? []).flatMap(
      (page) => page?.data?.interactions ?? [],
    );
    if (!channelInteractions.length) return interactions;

    return [...new Map(channelInteractions.map((item) => [item.id, item])).values()];
  }, [channelResponse?.pages, interactions]);

  const hasInsight = Boolean(summary || sentiment || bant);

  return (
    <div
      className={`flex flex-col min-h-0 h-full w-full font-sans bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden ${className}`}
    >
      <div className="min-h-0 flex-1 basis-0 overflow-y-auto  p-3 flex flex-col gap-4">
        {(hasInsight || isSummaryLoading) && (
          <div className="shrink-0 rounded-lg bg-secondary-light-transparent border border-secondary p-3">
            <div className="flex items-center justify-between gap-1.5">
              <span className="text-xs font-semibold text-secondary">
                Conversation Summary
              </span>
              {sentiment && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full ${sentimentClass(
                    sentiment.label,
                  )}`}
                >
                  {sentiment.positive_pct}% {sentiment.label}
                </span>
              )}
            </div>
            {isSummaryLoading && !summary && (
              <p className="mt-1 text-xs text-gray-400 flex gap-2 items-center justify-center w-full h-full">
                <Spinner size="sm" color="secondary" className="animate-spin" />{" "}
                Generating insights...
              </p>
            )}
            {summary && (
              <p className="mt-1 text-[0.69rem] text-secondary leading-relaxed">
                {summary}
              </p>
            )}
          </div>
        )}

        <BantSummary bant={bant} />

        <div className="flex flex-col">
          <div className="mb-2 flex shrink-0 items-center justify-between">
            <h3 className="text-xs font-semibold text-gray-800 dark:text-gray-200">
              Interaction history
            </h3>
            {!isLoading && (
              <span className="text-[11px] text-gray-400">
                {displayedInteractions.length} item
                {displayedInteractions.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {isLoading && (
            <div className="flex items-center justify-center h-full w-full">
              <Spinner size="sm" color="secondary" className="animate-spin" />
              <p className="text-gray-400 text-[12px] py-4 text-center">
                Syncing conversations...
              </p>
            </div>
          )}

          {!isLoading && displayedInteractions.length === 0 && (
            connectMeetings ? (
              <div className="flex flex-col items-center gap-1 py-8 text-center">
                <p className="text-gray-400 text-[12px]">
                  Meetings come from your Microsoft calendar.
                </p>
                <TextButton
                  onClick={connectMeetings.onConnect}
                  isLoading={connectMeetings.isConnecting}
                  loadingText="Redirecting..."
                >
                  Connect Microsoft to see meetings
                </TextButton>
              </div>
            ) : (
              <p className="text-gray-400 text-[12px] py-8 text-center">
                No interactions matched this account yet.
              </p>
            )
          )}

          <div className="flex flex-col gap-3">
            {displayedInteractions?.map((interaction) => (
              <InteractionCard
                key={interaction.id}
                interaction={interaction}
                contacts={contacts}
              />
            ))}
          </div>

          {hasNextPage && (
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="mt-3 self-center rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-secondary hover:text-secondary disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
            >
              {isFetchingNextPage ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" color="secondary" className="animate-spin" />
                  Loading older interactions...
                </span>
              ) : (
                `Load older $interactions`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountManagement;
